import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate, daysLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, Users, CheckSquare, CalendarClock, Plus, Trash2, Check, ListChecks } from "lucide-react";

const KINDS = [
  { key: "call", label: "Call", icon: Phone },
  { key: "email", label: "Email", icon: Mail },
  { key: "meeting", label: "Meeting", icon: Users },
  { key: "task", label: "Task", icon: CheckSquare },
  { key: "deadline", label: "Deadline", icon: CalendarClock },
];
export const kindIcon = (k) => (KINDS.find((x) => x.key === k) || KINDS[3]).icon;

export function dueTone(days) {
  if (days === null || days === undefined) return "bg-slate-100 text-slate-500 border-slate-200";
  if (days < 0) return "bg-rose-50 text-rose-700 border-rose-200";
  if (days === 0) return "bg-amber-50 text-amber-700 border-amber-200";
  if (days <= 3) return "bg-amber-50/70 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const dstr = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const daysOf = (iso) => (iso ? Math.round((new Date(dstr(iso)) - new Date(todayStr())) / 86400000) : null);

// Reusable activity list + inline "next step" scheduler for a lead or bid (Pipedrive model).
export default function ActivityPanel({ entityType, entityId, readOnly, onChange, className = "" }) {
  const [acts, setActs] = useState([]);
  const [kind, setKind] = useState("call");
  const [subject, setSubject] = useState("");
  const [due, setDue] = useState(todayStr());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!entityId) return;
    api.get("/crm/activities", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setActs(r.data || [])).catch(() => {});
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!subject.trim()) { toast.error("Add a subject for the follow-up."); return; }
    setBusy(true);
    try {
      await api.post("/crm/activities", {
        entity_type: entityType, entity_id: entityId, kind, subject: subject.trim(),
        due_at: due ? new Date(due + "T12:00:00").toISOString() : null,
      });
      setSubject(""); setDue(todayStr()); load(); onChange?.();
      toast.success("Next step scheduled");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
    finally { setBusy(false); }
  };
  const toggleDone = async (a) => {
    try { await api.post(`/crm/activities/${a.activity_id}/done`, { done: !a.done }); load(); onChange?.(); }
    catch { toast.error("Could not update"); }
  };
  const reschedule = async (a, v) => {
    try { await api.put(`/crm/activities/${a.activity_id}`, { due_at: v ? new Date(v + "T12:00:00").toISOString() : null }); load(); onChange?.(); }
    catch { toast.error("Could not reschedule"); }
  };
  const remove = async (a) => {
    try { await api.delete(`/crm/activities/${a.activity_id}`); load(); onChange?.(); }
    catch { toast.error("Could not delete"); }
  };

  const open = acts.filter((a) => !a.done);
  const done = acts.filter((a) => a.done);

  return (
    <div className={`space-y-2 ${className}`} data-testid="activity-panel">
      <div className="flex items-center gap-2">
        <ListChecks className="w-4 h-4 text-[#003E87]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Next steps &amp; follow-ups</span>
        {open.length > 0 && <span className="text-[10px] font-mono text-slate-400">{open.length} open</span>}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="activity-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Call PM about award timing"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }} className="h-8 flex-1 min-w-[160px] text-xs" data-testid="activity-subject" />
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-8 w-36 text-xs" data-testid="activity-due" />
          <Button size="sm" onClick={add} disabled={busy} className="h-8 bg-[#003E87] hover:bg-[#00305F]" data-testid="activity-add"><Plus className="w-3.5 h-3.5 mr-1" />Schedule</Button>
        </div>
      )}

      {open.length === 0 && done.length === 0 && (
        <div className="text-xs text-slate-400 px-1 py-2" data-testid="activity-empty">No follow-ups scheduled yet — a pursuit with no next step tends to go cold.</div>
      )}

      {open.map((a) => {
        const Icon = kindIcon(a.kind);
        const days = daysOf(a.due_at);
        return (
          <div key={a.activity_id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5" data-testid={`activity-row-${a.activity_id}`}>
            {!readOnly && (
              <button onClick={() => toggleDone(a)} title="Mark done" data-testid={`activity-done-${a.activity_id}`}
                className="w-4 h-4 rounded border border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 grid place-items-center shrink-0 transition-colors" />
            )}
            <Icon className="w-3.5 h-3.5 text-[#003E87] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-800 truncate">{a.subject}</div>
              {a.owner_name && <div className="text-[10px] text-slate-400 truncate">{a.owner_name}</div>}
            </div>
            {readOnly ? (
              <span className={`text-[10px] rounded px-1.5 py-0.5 border ${dueTone(days)}`}>{a.due_at ? daysLabel(days) : "no date"}</span>
            ) : (
              <input type="date" value={dstr(a.due_at)} onChange={(e) => reschedule(a, e.target.value)}
                className={`text-[10px] rounded px-1.5 py-0.5 border outline-none ${dueTone(days)}`} data-testid={`activity-reschedule-${a.activity_id}`} />
            )}
            {!readOnly && (
              <button onClick={() => remove(a)} title="Delete" data-testid={`activity-del-${a.activity_id}`} className="text-slate-300 hover:text-rose-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        );
      })}

      {done.length > 0 && (
        <div className="pt-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 px-1 mb-1">Done</div>
          {done.slice(0, 5).map((a) => {
            const Icon = kindIcon(a.kind);
            return (
              <div key={a.activity_id} className="flex items-center gap-2 px-2.5 py-1" data-testid={`activity-done-row-${a.activity_id}`}>
                {!readOnly && (
                  <button onClick={() => toggleDone(a)} title="Reopen" className="w-4 h-4 rounded bg-emerald-500 text-white grid place-items-center shrink-0"><Check className="w-3 h-3" /></button>
                )}
                <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <div className="text-xs text-slate-500 line-through truncate flex-1">{a.subject}</div>
                <span className="text-[10px] text-slate-400 shrink-0">{fmtDate(a.done_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
